import { useEffect, useState } from 'react'

export type NavStyle = 'sidebar' | 'sidebar-mini' | 'topbar'

const ALL_STYLES: readonly NavStyle[] = ['sidebar', 'sidebar-mini', 'topbar'] as const
const DEFAULT_STYLE: NavStyle = 'sidebar'
const USER_OVERRIDE_KEY = 'cravo-vendor-nav-style'
const STYLE_CHANGED_EVENT = 'cravo-nav-style-changed'

function isNavStyle(value: unknown): value is NavStyle {
  return typeof value === 'string' && (ALL_STYLES as readonly string[]).includes(value)
}

/**
 * Resolution order (highest → lowest):
 *   1. localStorage user override (`cravo-vendor-nav-style`) — lets the
 *      logged-in vendor pick a different style than their tenant's default.
 *   2. window.__TENANT_CONFIG__.branding.layout.nav_style — set by the
 *      tenant in the LabelGenic admin panel.
 *   3. 'sidebar' — built-in default (matches pre-feature behavior so any
 *      tenant that doesn't ship a value sees the existing UI).
 */
export function resolveNavStyle(): NavStyle {
  if (typeof window === 'undefined') return DEFAULT_STYLE

  try {
    const override = window.localStorage.getItem(USER_OVERRIDE_KEY)
    if (isNavStyle(override)) return override
  } catch {
    /* storage blocked — fall through */
  }

  const tenantValue = (window.__TENANT_CONFIG__ as
    | { branding?: { layout?: { nav_style?: unknown } } }
    | undefined)?.branding?.layout?.nav_style
  if (isNavStyle(tenantValue)) return tenantValue

  return DEFAULT_STYLE
}

/**
 * React hook for components that need to re-render when the nav style
 * changes. Listens for the cross-component `cravo-nav-style-changed`
 * CustomEvent dispatched by `setNavStyleOverride()`, so a settings page
 * can flip the style and every layout consumer updates synchronously.
 */
export function useNavStyle(): NavStyle {
  const [style, setStyle] = useState<NavStyle>(() => resolveNavStyle())

  useEffect(() => {
    const handler = () => setStyle(resolveNavStyle())
    window.addEventListener(STYLE_CHANGED_EVENT, handler)
    // Also listen for storage events so another tab's change propagates.
    window.addEventListener('storage', (event) => {
      if (event.key === USER_OVERRIDE_KEY) handler()
    })
    return () => {
      window.removeEventListener(STYLE_CHANGED_EVENT, handler)
    }
  }, [])

  return style
}

/**
 * Persist the user's nav-style choice and notify other components.
 * Pass `null` to clear the override and fall back to the tenant default.
 */
export function setNavStyleOverride(style: NavStyle | null): void {
  if (typeof window === 'undefined') return
  try {
    if (style === null) {
      window.localStorage.removeItem(USER_OVERRIDE_KEY)
    } else {
      window.localStorage.setItem(USER_OVERRIDE_KEY, style)
    }
  } catch {
    /* storage blocked — best-effort */
  }
  window.dispatchEvent(new CustomEvent(STYLE_CHANGED_EVENT))
}

export const NAV_STYLE_USER_OVERRIDE_KEY = USER_OVERRIDE_KEY
export const NAV_STYLE_OPTIONS = ALL_STYLES
